import { TGTMInfo } from 'types/models/product';
import eventBus from 'widgets/toolbox/eventBus';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { get } from 'widgets/toolbox/util';
import {
    trackCartUpdate, trackProductClick, getTilePosition, pageProperties, report, getProductList
} from '../utils/base';
import {
    getWishlistImpressions, impressionsDebounce, addToImpressedProducts, reportImpression, TImpression
} from './impressions';
/**
 * @description GTM events
 * @module events
 * @category widgets
 * @subcategory int_gtm
 */
let shippingMethodName = '';

/**
 * @description Init GTM events
 */
export function init(): void {
    /**
     * @description Tracks in dataLayer an information about product impressions on product tile init
     * @event "product.tile.init"
     * @listens "product.tile.init"
     */
    eventBus.on('product.tile.init', (tile: TRefElementInstance) => {
        const gtmInfoData = tile.data<TGTMInfo>('analytics');

        if (!gtmInfoData) {
            return;
        }

        const position = getTilePosition(gtmInfoData, { shouldBeNotImpressed: true });

        if (position) {
            const impression: TImpression = {
                ...gtmInfoData,
                index: position
            };

            if (!impression.item_list_name) {
                impression.item_list_name = getProductList(tile);
            }

            addToImpressedProducts(impression);

            impressionsDebounce.push(impression);

            reportImpression();
        }
    });
    /**
     * @description Tracks in dataLayer an information about product impressions on comparison bucket init
     * @event "comparison.items.init"
     * @listens "comparison.items.init"
     */
    eventBus.on('comparison.items.init', (comparisonItems, comparisonBucketContainer) => {
        const gtmComparisonItems = comparisonItems.map((comparisonItem) => {
            const impression: TImpression = {
                quantity: 1,
                item_id: comparisonItem.id,
                item_name: comparisonItem.name,
                item_category: comparisonItem.category,
                item_list_name: getProductList(comparisonBucketContainer),
                price: comparisonItem.price?.sales?.value || comparisonItem.price?.min?.sales?.value || 'N\\A'
            };

            return impression;
        });

        if (gtmComparisonItems.length > 0) {
            report('event', 'view_item_list', {
                currency: pageProperties.currentCurrency,
                items: gtmComparisonItems
            });
        }
    });
    /**
     * @description Tracks in dataLayer an information about product impressions on wishlist products init
     * @event "wishlist.products.init"
     * @listens "wishlist.products.init"
     */
    eventBus.on('wishlist.products.init', (products, step) => {
        const impressions = getWishlistImpressions(products);

        if (!impressions.length) {
            return;
        }

        let event = 'pageLoad';

        if (step && (step === 'browsing')) {
            event = 'view_item_list';
        }

        report('event', event, {
            currency: pageProperties.currentCurrency,
            items: impressions
        });
    });

    /**
     * @description Tracks in dataLayer an information about adding products to wishlist during removing from minicart
     * @event "minicart.product.added.to.wishlist"
     * @listens "minicart.product.added.to.wishlist"
     */
    eventBus.on('minicart.product.added.to.wishlist', minicart => {
        if (!minicart.removeProductLink) {
            return;
        }

        const tileInfo = minicart.removeProductLink.data('analytics');

        if (!tileInfo) {
            return;
        }

        delete tileInfo.combinedBonusGtmInfo;

        tileInfo.quantity = parseInt(minicart.removeProductLink.data('quantity'), 10) || 1;

        report('event', 'add_to_wishlist', {
            currency: pageProperties.currentCurrency,
            items: [tileInfo]
        });
    });

    /**
     * @description Tracks in dataLayer an information about adding products to wishlist during removing from cart
     * @event "cart.product.added.to.wishlist"
     * @listens "cart.product.added.to.wishlist"
     */
    eventBus.on('cart.product.added.to.wishlist', cart => {
        if (!cart.removeButton) {
            return;
        }

        const tileInfo = cart.removeButton.data('analytics');

        if (!tileInfo) {
            return;
        }

        tileInfo.quantity = parseInt(cart.removeButton.data('quantity'), 10) || 1;

        delete tileInfo.list;
        delete tileInfo.combinedBonusGtmInfo;

        report('event', 'add_to_wishlist', {
            currency: pageProperties.currentCurrency,
            items: [tileInfo]
        });
    });

    /**
     * @description Tracks in dataLayer an information about adding products to wishlist
     * @event "product.added.to.wishlist"
     * @listens "product.added.to.wishlist"
     */
    eventBus.on('product.added.to.wishlist', (dataSource) => {
        let gtmInfo;

        if (dataSource.mixedToProductDetails() && dataSource.currentGtmInfo) {
            gtmInfo = dataSource.currentGtmInfo;
        } else {
            gtmInfo = dataSource.data('analytics');
        }

        if (!gtmInfo) {
            return;
        }

        delete gtmInfo.combinedBonusGtmInfo;

        report('event', 'add_to_wishlist', {
            currency: pageProperties.currentCurrency,
            items: [gtmInfo]
        });
    });
    /**
     * @description Tracks in dataLayer an information about removing products from wishlist
     * @event "wishlist.product.removed"
     * @listens "wishlist.product.removed"
     * @param {TRefElementInstance} removedProduct - The product being removed from the wishlist.
     */
    eventBus.on('wishlist.product.removed', (removedProduct) => {
        const gtmInfo = removedProduct.data('analytics');

        if (!gtmInfo) {
            return;
        }

        delete gtmInfo.combinedBonusGtmInfo;

        report('event', 'remove_from_wishlist', {
            currency: pageProperties.currentCurrency,
            items: [gtmInfo]
        });
    });

    /**
     * @description Tracks in dataLayer an information about quick view opening
     * @event "product.tile.qv.open"
     * @listens "product.tile.qv.open"
     */
    eventBus.on('product.tile.qv.open', qvData => {
        report('event', 'quick_view_item', {
            item_list_name: 'Product quick view',
            currency: pageProperties.currentCurrency,
            value: get(qvData, 'gtmInfo.price'),
            items: [qvData.gtmInfo]
        });
    });
    /**
     * @description Tracks in dataLayer an information about quick view opening on editing product step
     * @event "product.tile.qv.open.edit"
     * @listens "product.tile.qv.open.edit"
     */
    eventBus.on('product.tile.qv.open.edit', (editEl) => {
        if (!editEl) {
            return;
        }

        const productInfo = editEl.data('analytics');

        if (!productInfo) {
            return;
        }

        report('event', 'view_item', {
            item_list_name: 'Product quick view',
            currency: pageProperties.currentCurrency,
            value: productInfo.price,
            items: [productInfo]
        });
    });
    /**
     * @description Tracks in dataLayer an information about ctaExt button click
     * @event "ctaExt.click"
     * @listens "ctaExt.click"
     * @returns {void}
     */
    eventBus.on('ctaExt.click', elem => {
        report('event', 'cta_ext_click', {
            targetUrl: elem.attr('href'),
            title: elem.prop('innerText') || elem.prop('title')
        });
    });
    /**
     * @description Tracks in dataLayer an information about video-banner action event
     * @event "videoPoster.click"
     * @listens "videoPoster.click"
     * @returns {void}
     */
    eventBus.on('videoPoster.click', (playerType, videoName) => {
        report('event', 'video_poster_click', {
            playerType: playerType,
            name: videoName
        });
    });
    /**
     * @description Tracks in dataLayer an information about video action event
     * @event "hotSpot.click"
     * @listens "hotSpot.click"
     * @returns {void}
     */
    eventBus.on('hotSpot.click', (titleText) => {
        report('event', 'hot_spot_click', {
            title: titleText
        });
    });
    /**
     * @description Tracks in dataLayer an information product carousel action event
     * @event "simpleCarousel.click"
     * @listens "simpleCarousel.click"
     * @returns {void}
     */
    eventBus.on('simpleCarousel.click', (actionType, slideData) => {
        report('event', 'simple_carousel_click', {
            item: slideData,
            action: actionType
        });
    });
    /**
     * @description Tracks in dataLayer an information hero carousel action event
     * @event "heroCarousel.click"
     * @listens "heroCarousel.click"
     * @returns {void}
     */
    eventBus.on('heroCarousel.click', (actionType, slideData) => {
        report('event', 'hero_carousel_click', {
            item: slideData,
            action: actionType
        });
    });
    /**
     * @description Tracks in dataLayer an information about product tile link click
     * @event "tile.product.link.click"
     * @listens "tile.product.link.click"
     */
    eventBus.on('tile.product.link.click', link => {
        trackProductClick(link);
    });
    /**
     * @description Tracks in dataLayer an information about color change on tile
     * @event "tile.color.changed"
     * @listens "tile.color.changed"
     */
    eventBus.on('tile.color.changed', link => {
        trackProductClick(link, 'color_click');
    });
    /**
     * @description Tracks in dataLayer an information about product details link click
     * @event "detail.product.link.click"
     * @listens "detail.product.link.click"
     */
    eventBus.on('detail.product.link.click', link => {
        trackProductClick(link);
    });
    /**
     * @description Tracks in dataLayer an information about product details size guide link click
     * @event "detail.product.size.guide.link.click"
     * @listens "detail.product.size.guide.link.click"
     */
    eventBus.on('detail.product.size.guide.link.click', dataSource => {
        let gtmInfo;

        if (dataSource.currentGtmInfo) {
            gtmInfo = dataSource.currentGtmInfo;
        } else {
            gtmInfo = dataSource.data('analytics');
        }

        if (!gtmInfo) {
            return;
        }

        report('event', 'click_on_size_guide', {
            currency: pageProperties.currentCurrency,
            items: [gtmInfo]
        });
    });
    /**
     * @description Tracks in dataLayer an information about product details image click
     * @event "detail.product.image.click"
     * @listens "detail.product.image.click"
     */
    eventBus.on('detail.product.image.click', dataSource => {
        let gtmInfo;

        if (dataSource.currentGtmInfo) {
            gtmInfo = dataSource.currentGtmInfo;
        } else {
            gtmInfo = dataSource.data('analytics');
        }

        if (!gtmInfo) {
            return;
        }

        report('event', 'image_click', {
            currency: pageProperties.currentCurrency,
            items: [gtmInfo]
        });
    });
    /**
     * @description Tracks in dataLayer an information about searchbox product tile link click
     * @event "searchbox.product.link.click"
     * @listens "searchbox.product.link.click"
     */
    eventBus.on('searchbox.product.link.click', link => {
        trackProductClick(link);
    });
    /**
     * @description Tracks in dataLayer an information about wishlist product tile link click
     * @event "wishlist.product.link.click"
     * @listens "wishlist.product.link.click"
     */
    eventBus.on('wishlist.product.link.click', link => {
        trackProductClick(link);
    });
    /**
     * @description Tracks in dataLayer an information about comparison product tile link click
     * @event "comparison.product.link.click"
     * @listens "comparison.product.link.click"
     */
    eventBus.on('comparison.product.link.click', link => {
        trackProductClick(link);
    });
    /**
     * @description Tracks in dataLayer an information about product variation selected
     * @event "product.variation.select"
     * @listens "product.variation.select"
     */
    eventBus.on('product.variation.select', (productVariantSelect, data) => {
        const gtmInfo = productVariantSelect.currentGtmInfo || productVariantSelect.data('analytics');

        if (!gtmInfo) {
            return;
        }

        if (data.id === 'attr-color') {
            report('event', 'color_click', {
                currency: pageProperties.currentCurrency,
                items: [gtmInfo],
                shop_the_look: productVariantSelect.prefs().hasShopTheLook
            });
        } else {
            report('event', 'select_size', {
                currency: pageProperties.currentCurrency,
                items: [gtmInfo],
                size_type: data.displayName,
                size_value: data.displayValue
            });
        }
    });
    /**
     * @description Tracks in dataLayer an information about adding products to cart
     * @event "product.added.to.cart"
     * @listens "product.added.to.cart"
     */
    eventBus.on('product.added.to.cart', (cart, widget) => {
        const tileInfo = widget.currentGtmInfo || widget.data('analytics');

        if (!tileInfo) {
            return;
        }

        if (!tileInfo.quantity) {
            tileInfo.quantity = 1;
        }

        report('event', 'add_to_cart', {
            currency: pageProperties.currentCurrency,
            value: tileInfo.price,
            items: [tileInfo]
        });

        if (cart && cart.items) {
            trackCartUpdate(cart);
        }
    });
    /**
     * @description Tracks in dataLayer an information about bonus products added to cart
     * @event "bonus.product.added.to.cart"
     * @listens "bonus.product.added.to.cart"
     */
    eventBus.on('bonus.product.added.to.cart', (cart, widget) => {
        const tileInfo = widget.bonusGtmInfo;

        if (!tileInfo || !tileInfo.length) {
            return;
        }

        report('event', 'add_to_cart', {
            currency: pageProperties.currentCurrency,
            value: '0',
            items: tileInfo
        });

        if (cart && cart.items) {
            trackCartUpdate(cart);
        }
    });
    /**
     * @description Tracks in dataLayer an information about updates on cart
     * @event "cart.updated"
     * @listens "cart.updated"
     */
    eventBus.on('cart.updated', cart => {
        if (cart && cart.items) {
            trackCartUpdate(cart);
        }
    });
    /**
     * @description Tracks in dataLayer an information about updates on cart
     * @event "cart.updated"
     * @listens "cart.updated"
     */
    eventBus.on('minicart.updated', cart => {
        if (cart && cart.items) {
            trackCartUpdate(cart);
        }
    });
    /**
     * @description Tracks in dataLayer an information about removing products from cart
     * @event "cart.remove.product"
     * @listens "cart.remove.product"
     */
    eventBus.on('cart.remove.product', cart => {
        if (!cart.removeButton) {
            return;
        }

        const tileInfo = cart.removeButton.data('analytics');

        if (!tileInfo) {
            return;
        }

        const bonusTileInfo = tileInfo.combinedBonusGtmInfo || [];

        tileInfo.quantity = parseInt(cart.removeButton.data('quantity'), 10) || 1;
        delete tileInfo.list;
        delete tileInfo.combinedBonusGtmInfo;

        report('event', 'remove_from_cart', {
            currency: pageProperties.currentCurrency,
            value: tileInfo.price,
            items: [tileInfo].concat(bonusTileInfo)
        });
    });
    /**
     * @description Tracks in dataLayer an information about removing products from minicart
     * @event "minicart.remove.product"
     * @listens "minicart.remove.product"
     */
    eventBus.on('minicart.remove.product', minicart => {
        if (!minicart.removeProductLink) {
            return;
        }

        const tileInfo = minicart.removeProductLink.data('analytics');

        if (!tileInfo) {
            return;
        }

        const bonusTileInfo = tileInfo.combinedBonusGtmInfo || [];

        delete tileInfo.combinedBonusGtmInfo;

        tileInfo.quantity = parseInt(minicart.removeProductLink.data('quantity'), 10) || 1;

        report('event', 'remove_from_cart', {
            currency: pageProperties.currentCurrency,
            value: tileInfo.price,
            items: [].concat(tileInfo, bonusTileInfo)
        });
    });
    /**
     * @description Tracks in dataLayer an information about selected shipping method
     * @event "cart.update.shipping.method"
     * @listens "cart.update.shipping.method"
     */
    eventBus.on('cart.update.shipping.method', (order) => {
        const newShippingMethodName = order.shipping[0].selectedShippingMethod.displayName;

        if (shippingMethodName === newShippingMethodName) {
            return;
        }

        shippingMethodName = newShippingMethodName;
        report('event', 'add_shipping_info', {
            currency: pageProperties.currentCurrency,
            shipping_tier: shippingMethodName,
            value: order.gtmInfo.totals.totalPrice,
            items: order.gtmInfo.products,
            step: 1
        });
    });
    /**
     * @description Tracks in dataLayer an information about customer's opening a customer step
     * @event "checkout.customer.login"
     * @listens "checkout.customer.login"
     */
    eventBus.on('checkout.customer.login', checkoutMgr => {
        if (checkoutMgr.order && checkoutMgr.order.gtmInfo) {
            report('event', 'checkout', {
                currency: pageProperties.currentCurrency,
                step: 2,
                items: checkoutMgr.order.gtmInfo.products
            });
        }
    });
    /**
     * @description Tracks in dataLayer an information about customer's filling a shipping step
     * @event "checkout.shipping.step.activated"
     * @listens "checkout.shipping.step.activated"
     */
    eventBus.on('checkout.shipping.step.activated', checkoutMgr => {
        if (checkoutMgr.order && checkoutMgr.order.gtmInfo) {
            shippingMethodName = get(checkoutMgr, 'order.shipping.0.selectedShippingMethod.displayName');

            if (!shippingMethodName) {
                const applicableShippingMethods = get(checkoutMgr, 'order.shipping.0.applicableShippingMethods') || [];

                applicableShippingMethods.forEach(applicableShippingMethod => {
                    if (applicableShippingMethod && applicableShippingMethod.selected) {
                        shippingMethodName = applicableShippingMethod.displayName;
                    }
                });
            }

            report('event', 'checkout', {
                currency: pageProperties.currentCurrency,
                value: checkoutMgr.order.gtmInfo.totals.totalPrice,
                shipping_tier: shippingMethodName,
                items: checkoutMgr.order.gtmInfo.products,
                step: 3
            });

            report('event', 'add_shipping_info', {
                currency: pageProperties.currentCurrency,
                shipping_tier: shippingMethodName,
                value: checkoutMgr.order.gtmInfo.totals.totalPrice,
                items: checkoutMgr.order.gtmInfo.products,
                step: 3
            });
        }
    });
    /**
     * @description Tracks in dataLayer an information about selected shipping method on checkout
     * @event "checkout.update.shipping.method"
     * @listens "checkout.update.shipping.method"
     */
    eventBus.on('checkout.update.shipping.method', (order) => {
        const newShippingMethodName = order.shipping[0].selectedShippingMethod.displayName;

        if (shippingMethodName === newShippingMethodName) {
            return;
        }

        shippingMethodName = newShippingMethodName;
        report('event', 'add_shipping_info', {
            currency: pageProperties.currentCurrency,
            shipping_tier: shippingMethodName,
            value: order.gtmInfo.totals.totalPrice,
            items: order.gtmInfo.products,
            step: 3
        });
    });
    /**
     * @description Tracks in dataLayer an information about changed payment method
     * @event "checkout.paymentmethod.changed"
     * @listens "checkout.paymentmethod.changed"
     */
    eventBus.on('checkout.paymentmethod.changed', (paymentMethodName, basket) => {
        if (!paymentMethodName) {
            return;
        }

        report('event', 'add_payment_info', {
            currency: pageProperties.currentCurrency,
            payment_type: paymentMethodName,
            items: basket.gtmInfo.products,
            value: basket.gtmInfo.totals.totalPrice,
            step: 4
        });
    });
    /**
     * @description Tracks in dataLayer an information about customer's filling a billing step
     * @event "checkout.billing.step.activated"
     * @listens "checkout.billing.step.activated"
     */
    eventBus.on('checkout.billing.step.activated', checkoutMgr => {
        if (checkoutMgr.order && checkoutMgr.order.gtmInfo) {
            let paymentMethod = get(checkoutMgr, 'order.billing.payment.selectedPaymentInstruments.0.name');

            if (!paymentMethod) {
                paymentMethod = get(checkoutMgr, 'order.billing.payment.applicablePaymentMethods.0.name') || '';
            }

            report('event', 'checkout', {
                currency: pageProperties.currentCurrency,
                shipping_tier: shippingMethodName,
                step: 4,
                option: paymentMethod,
                items: checkoutMgr.order.gtmInfo.products
            });
        }
    });
    /**
     * @description Tracks in dataLayer an information about customer's submitting summary step
     * @event "checkout.summary.step.activated"
     * @listens "checkout.summary.step.activated"
     */
    eventBus.on('checkout.summary.step.activated', checkoutMgr => {
        if (checkoutMgr.order && checkoutMgr.order.gtmInfo) {
            report('event', 'checkout', {
                currency: pageProperties.currentCurrency,
                shipping_tier: shippingMethodName,
                step: 5,
                items: checkoutMgr.order.gtmInfo.products
            });
        }
    });
    /**
     * @description Sends add_to_cart or remove_from_cart event on product quantity change
     * @event "product.quantity.changed"
     * @listens "product.quantity.changed"
     */
    eventBus.on('product.quantity.changed', inputStepper => {
        const tileInfo = inputStepper.data('analytics');

        if (!tileInfo) {
            return;
        }

        const previousValue = parseInt(inputStepper.previousValue, 10);
        const currentValue = parseInt(inputStepper.getValue(), 10);

        if (previousValue < currentValue) {
            tileInfo.quantity = currentValue - previousValue;

            report('event', 'add_to_cart', {
                currency: pageProperties.currentCurrency,
                value: tileInfo.price,
                items: [tileInfo]
            });
        } else if (previousValue - currentValue > 0) {
            tileInfo.quantity = previousValue - currentValue;
            report('event', 'remove_from_cart', {
                currency: pageProperties.currentCurrency,
                value: tileInfo.price,
                items: [tileInfo]
            });
        }
    });
    /**
     * @description Tracks in dataLayer an information about logged in user
     * @event "customer.login"
     * @listens "customer.login"
     */
    eventBus.on('customer.login', data => {
        report('event', 'login', {
            ...data?.analytics?.userData
        });
    });
    /**
     * @description Tracks in dataLayer an information about registered user
     * @event "customer.registered"
     * @listens "customer.registered"
     */
    eventBus.on('customer.registered', data => {
        report('event', 'sign_up', {
            ...data?.analytics?.userData
        });
    });
    /**
     * @description Tracks in dataLayer an information about newsletter subscription
     * @event "newsletter.confirmed"
     * @listens "newsletter.confirmed"
     */
    eventBus.on('newsletter.confirmed', email => {
        report('event', 'newsletter_confirmed', {
            emailAddress: email
        });
    });
    /**
     * @description Tracks in dataLayer an information about refinement filter apply
     * @event "refinement.filter.apply"
     * @listens "refinement.filter.apply"
     */
    eventBus.on('refinement.filter.apply', (filterDisplayName, filterValue) => {
        report('event', 'filter_apply', {
            filter_category: filterDisplayName,
            filter_value: filterValue
        });
    });
    /**
     * @description Tracks in dataLayer an information about refinement filter remove
     * @event "refinement.filter.remove"
     * @listens "refinement.filter.remove"
     */
    eventBus.on('refinement.filter.remove', (filterDisplayName, filterValue) => {
        report('event', 'filter_remove', {
            filter_category: filterDisplayName,
            filter_value: filterValue
        });
    });
    /**
     * @description Tracks in dataLayer an information about refinement filter clear all
     * @event "refinement.clear.all.filter"
     * @listens "refinement.clear.all.filter"
     */
    eventBus.on('refinement.clear.all.filter', () => {
        report('event', 'filter_clear_all');
    });
    /**
     * @description Tracks in dataLayer an information about sort by filter
     * @event "sort.by.filter"
     * @listens "sort.by.filter"
     */
    eventBus.on('sort.by.filter', sortValue => {
        report('event', 'sort_items', {
            sort_value: sortValue
        });
    });
}
